import { Modal, TreeSelect, message, Button } from 'antd'
import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchAccountVenues } from 'src/features/AccountsSlice'
import { checkIngredients, shareRecipe } from 'src/features/RecipeBuilderSlice'
import { claimSend, recipeShareToast } from 'src/utils/recipeShareResult'
import {
  classifyVenuesForRecipeShare,
  resolveShareableVenueIds,
} from '../../utils/classifyShareVenues'

const ShareRecipeModal = ({
  showShareRecipeModal,
  setShowShareRecipeModal,
  recipeId,
}) => {
  const dispatch = useDispatch()
  const venues = useSelector(state => state.accounts.accountVenues)
  const [selectedVenueIds, setSelectedVenuesIds] = useState([])
  const [modalContent, setModalContent] = useState(null)
  const [finalVenueIdsToShare, setFinalVenueIdsToShare] = useState([])
  const [cannotShareVenuesCount, setCannotShareVenuesCount] = useState(0)
  const [shareLoading, setShareLoading] = useState(false)
  const sendingRef = useRef(false)
  const username = useSelector(state => state.auth.cognito.username)
  const { isLoading } = useSelector(state => state.recipes)

  useEffect(() => {
    dispatch(fetchAccountVenues({ username }))
  }, [dispatch, username])

  const venueOptions = venues?.map(item => ({
    title: item.title,
    value: item._id,
  }))

  const allIds = venueOptions?.map(({ value }) => value)

  const venueTitleToId = (venues || []).reduce((acc, venue) => {
    if (venue?.title && venue?._id) {
      acc[venue.title] = venue._id
    }
    return acc
  }, {})

  const handleSubmit = async () => {
    if (!selectedVenueIds.length) {
      message.warning('Please select at least one venue.')
      return
    }

    const payload = await dispatch(
      checkIngredients({ venueIds: selectedVenueIds, recipeId }),
    )

    if (!payload?.success) {
      message.error(
        payload?.message || 'Could not check ingredients. Please try again.',
      )
      return
    }

    if (payload?.success) {
      const {
        cannotShare,
        canAddAndShare,
        canShareDirectly,
        canShareDirectlyNoOms,
        unclassifiedVenues,
        shareableVenueTitles,
      } = classifyVenuesForRecipeShare(payload.missingMap)

      unclassifiedVenues.forEach(venue => {
        console.warn('shareRecipe: unclassified venue after ingredient check', {
          venue,
          info: payload.missingMap?.[venue],
        })
      })

      if (unclassifiedVenues.length > 0) {
        message.warning(
          `Could not determine share status for: ${unclassifiedVenues.join(
            ', ',
          )}`,
        )
      }

      setCannotShareVenuesCount(cannotShare?.length || 0)
      const content = (
        <div style={{ lineHeight: 1.6 }}>
          {canAddAndShare.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <p style={{ fontWeight: 500, color: 'green' }}>
                ✅ The recipe will be shared with the following venues after
                adding missing ingredients to their shopping list:
              </p>
              <ul style={{ paddingLeft: 20 }}>
                {canAddAndShare.map(item => (
                  <li key={item.venue} style={{ marginBottom: 8 }}>
                    <span style={{ fontWeight: 600 }}>{item.venue}</span>
                    <div style={{ marginLeft: 16 }}>
                      <span>Missing Ingredients:</span>
                      <ul style={{ paddingLeft: 16, marginTop: 4 }}>
                        {item.notInShoppingList?.map(ing => (
                          <li
                            key={`${item.venue}-${ing.sku}`}
                            style={{ fontSize: 14 }}
                          >
                            <code
                              style={{
                                backgroundColor: '#f4f4f4',
                                padding: '2px 4px',
                                borderRadius: 4,
                              }}
                            >
                              {ing.sku}
                            </code>{' '}
                            {ing.title}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {canShareDirectlyNoOms.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <p style={{ fontWeight: 500, color: 'green' }}>
                ✅ The recipe will be shared directly with the following venues
                (no OMS ingredients to validate):
              </p>
              <ul style={{ paddingLeft: 20 }}>
                {canShareDirectlyNoOms.map(v => (
                  <li key={v} style={{ fontSize: 14 }}>
                    {v}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {canShareDirectly.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <p style={{ fontWeight: 500, color: 'green' }}>
                ✅ The recipe will be shared directly with the following venues
                (all ingredients already exist in their shopping list):
              </p>
              <ul style={{ paddingLeft: 20 }}>
                {canShareDirectly.map(v => (
                  <li key={v} style={{ fontSize: 14 }}>
                    {v}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {cannotShare.length > 0 && (
            <div>
              <p style={{ fontWeight: 500, color: 'red' }}>
                ❌ The recipe cannot be shared with the following venues
                (missing ingredients not found in catalogue or shopping list):
              </p>
              <ul style={{ paddingLeft: 20 }}>
                {cannotShare.map(item => (
                  <li key={item.venue} style={{ marginBottom: 8 }}>
                    <span style={{ fontWeight: 600 }}>{item.venue}</span>
                    <div style={{ marginLeft: 16 }}>
                      <span>Missing Ingredients:</span>
                      <ul style={{ paddingLeft: 16, marginTop: 4 }}>
                        {item.notInCatalogue?.map(ing => (
                          <li
                            key={`${item.venue}-${ing.sku}`}
                            style={{ fontSize: 14 }}
                          >
                            <code
                              style={{
                                backgroundColor: '#fce8e6',
                                padding: '2px 4px',
                                borderRadius: 4,
                              }}
                            >
                              {ing.sku}
                            </code>{' '}
                            {ing.title}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {cannotShare.length > 0 ? (
            <div style={{ marginTop: 18 }}>
              <p style={{ fontWeight: 600, color: '#ad8b00' }}>
                ⚠ Experienced users can force the share anyway. Unmatched
                ingredients will be skipped, and the destination recipe will be
                flagged for review.
              </p>
            </div>
          ) : null}
        </div>
      )

      setModalContent(content)
      setFinalVenueIdsToShare(
        resolveShareableVenueIds(shareableVenueTitles, venueTitleToId),
      )
    }
  }

  const resetAndClose = () => {
    setSelectedVenuesIds([])
    setModalContent(null)
    setFinalVenueIdsToShare([])
    setCannotShareVenuesCount(0)
    setShowShareRecipeModal(false)
  }

  const sendShare = async (venueIdsToShare, { forced }) => {
    if (!claimSend(sendingRef)) return
    setShareLoading(true)
    try {
      const result = await dispatch(
        shareRecipe(recipeId, venueIdsToShare, { forceShare: forced }),
      )
      if (!result?.success) {
        message.error(
          result?.message ||
            (forced
              ? 'Failed to force share recipe. Please try again.'
              : 'Failed to share recipe. Please try again.'),
        )
        return
      }
      const toast = recipeShareToast(result, { forced })
      if (toast) message.success(toast)
      resetAndClose()
    } finally {
      sendingRef.current = false
      setShareLoading(false)
    }
  }

  const handleNormalShare = async () => {
    if (!finalVenueIdsToShare.length) {
      message.error(
        'The recipe could not be shared with the selected venues. Please check missing ingredients.',
      )
      return
    }
    await sendShare(finalVenueIdsToShare, { forced: false })
  }

  const handleForceShare = async () => {
    if (!selectedVenueIds.length) return
    await sendShare(selectedVenueIds, { forced: true })
  }

  return (
    <Modal
      width={modalContent ? 800 : 600}
      confirmLoading={isLoading}
      title='Share Recipe'
      visible={showShareRecipeModal}
      onCancel={() => {
        resetAndClose()
      }}
      footer={
        modalContent
          ? [
              <Button key='cancel' onClick={() => resetAndClose()}>
                Cancel
              </Button>,
              <Button
                key='normal'
                type='primary'
                onClick={() => void handleNormalShare()}
                disabled={!finalVenueIdsToShare.length || shareLoading}
                loading={shareLoading}
              >
                Share Recipe
              </Button>,
              cannotShareVenuesCount > 0 ? (
                <Button
                  key='force'
                  type='primary'
                  danger
                  onClick={() => void handleForceShare()}
                  disabled={shareLoading}
                  loading={shareLoading}
                >
                  Force Share Anyway
                </Button>
              ) : null,
            ]
          : undefined
      }
      onOk={modalContent ? undefined : handleSubmit}
    >
      {modalContent ? (
        modalContent
      ) : (
        <TreeSelect
          treeNodeFilterProp='title'
          allowClear={true}
          placeholder='Select Venues ...'
          treeCheckable={true}
          showCheckedStrategy={TreeSelect.SHOW_CHILD}
          dropdownStyle={{ maxHeight: '300px' }}
          style={{ marginBottom: '15px', width: '355px' }}
          onChange={ids => setSelectedVenuesIds(ids)}
          value={selectedVenueIds}
          maxTagCount={1}
          maxTagPlaceholder={omittedValues =>
            `+ ${omittedValues.length} Venues ...`
          }
          treeData={[
            {
              title:
                selectedVenueIds.length > 0 ? (
                  <span
                    onClick={() => setSelectedVenuesIds([])}
                    style={{
                      display: 'inline-block',
                      color: '#b5d922',
                      cursor: 'pointer',
                    }}
                  >
                    Unselect all
                  </span>
                ) : (
                  <span
                    onClick={() => setSelectedVenuesIds(allIds)}
                    style={{
                      display: 'inline-block',
                      color: '#b5d922',
                      cursor: 'pointer',
                    }}
                  >
                    Select all
                  </span>
                ),
              value: 'xxx',
              disableCheckbox: true,
              disabled: true,
            },
            ...venueOptions,
          ]}
        />
      )}
    </Modal>
  )
}

export default ShareRecipeModal
